import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { WorkingDays } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment-timezone'
import { BehaviorSubject, map, startWith, Subject } from 'rxjs'

@Component({
  selector: 'mmx-location-working-hours',
  templateUrl: './location-working-hours.component.html',
  styleUrls: ['./location-working-hours.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LocationWorkingHoursComponent implements OnChanges {
  @Input()
  public workingDays: WorkingDays = {}

  @Input()
  public timezone = 'America/New_York'

  private onChanges$ = new Subject<SimpleChanges>()

  public isOpenNow$ = this.onChanges$.pipe(
    startWith({}),
    map(() => this.isOpenNow()),
  )

  public openingOrClosingTime$ = this.onChanges$.pipe(
    startWith({}),
    map(() => this.getOpeningOrClosingTime()),
  )

  public formattedWorkingHours$ = this.onChanges$.pipe(
    startWith({}),
    map(() => this.getFormattedWorkingHours()),
  )

  public expanded$ = new BehaviorSubject<boolean>(false)

  constructor(
    private translateService: TranslateService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.onChanges$.next(changes)
  }

  public toggleExpand(event: MouseEvent) {
    event.stopPropagation()
    this.expanded$.next(!this.expanded$.value)
  }

  private isOpenNow() {
    const weekdayNow = this.getWeekdayNow()
    const timeNow = moment.tz(moment(), this.timezone)
    const hoursToday = this.workingDays[weekdayNow]

    if (!hoursToday?.openAt || !hoursToday?.closeAt) {
      return false
    }

    const startFrom = moment.tz(hoursToday.openAt, 'HH:mm', this.timezone)
    const endAt = moment.tz(hoursToday.closeAt, 'HH:mm', this.timezone)

    return timeNow.isBetween(startFrom, endAt)
  }

  private getOpeningOrClosingTime() {
    const weekdayNow = this.getWeekdayNow()
    const isOpenNow = this.isOpenNow()
    const hoursToday = this.workingDays[weekdayNow]

    if (!hoursToday?.openAt || !hoursToday?.closeAt) {
      return ''
    }

    const startFrom = moment.tz(hoursToday.openAt, 'HH:mm', this.timezone)
    const endAt = moment.tz(hoursToday.closeAt, 'HH:mm', this.timezone)

    if (isOpenNow) {
      return this.translateService.instant(
        'appointment-schedule.location.hours.closes-at',
        { time: endAt.format('h:mm A') },
      )
    }

    return this.translateService.instant(
      'appointment-schedule.location.hours.opens-at',
      { time: startFrom.format('h:mm A') },
    )
  }

  private getFormattedWorkingHours() {
    const daysOrder: (keyof WorkingDays)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

    return daysOrder.map(day => {
      const currentDay = this.workingDays[day]

      // Check if the currentDay is defined and has openAt and closeAt properties
      if (!currentDay || currentDay.isClosed || !currentDay.openAt || !currentDay.closeAt) {
        return {
          day: day.toUpperCase(),
          openAt: '',
          closeAt: '',
          isClosed: true,
        }
      }

      // At this point, TypeScript knows currentDay, openAt, and closeAt are defined
      return {
        day: day.toUpperCase(),
        openAt: moment.tz(currentDay.openAt, 'HH:mm', this.timezone).format('h:mm A'),
        closeAt: moment.tz(currentDay.closeAt, 'HH:mm', this.timezone).format('h:mm A'),
        isClosed: currentDay.isClosed,
      }
    })
  }

  private getWeekdayNow(): keyof WorkingDays {
    return moment
      .tz(moment(), this.timezone)
      .locale('en')
      .format('dddd')
      .toLowerCase()
      .substring(0, 3) as keyof WorkingDays
  }
}
